import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import Layout from "./common/Layout";
import Loading from "./common/Loading";
import { add_user } from "../apis/AddUser";
import "../css/addEditUser.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const AddEditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editUser = location.state?.user; // 如果是编辑模式，从路由 state 获取用户数据

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    role: "user",
    status: 1, // 1: 启用, 0: 禁用
    balance: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 如果是编辑模式，填充表单数据
  useEffect(() => {
    if (editUser) {
      setFormData({
        username: editUser.username,
        email: editUser.email,
        phone: editUser.phone,
        password: "", // 编辑模式不填充密码
        confirmPassword: "",
        role: editUser.role,
        status: editUser.status,
        balance: editUser.balance || 0,
      });
    }
  }, [editUser]);

  const handleBack = () => {
    navigate("/user-management");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // 表单验证
    if (!formData.username.trim()) {
      setError("请输入用户名");
      return;
    }
    if (!formData.email.trim()) {
      setError("请输入邮箱");
      return;
    }
    if (!formData.phone.trim()) {
      setError("请输入手机号");
      return;
    }

    // 添加用户时需要密码，编辑用户时密码可选
    if (!editUser) {
      if (!formData.password) {
        setError("请输入密码");
        return;
      }
      if (formData.password.length < 6) {
        setError("密码长度至少为 6 位");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError("两次输入的密码不一致");
        return;
      }
    } else {
      // 编辑模式下，如果填写了密码，需要验证
      if (formData.password) {
        if (formData.password.length < 6) {
          setError("密码长度至少为 6 位");
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError("两次输入的密码不一致");
          return;
        }
      }
    }

    try {
      setLoading(true);

      // 准备请求参数
      const params = editUser
        ? {
            user_id: editUser.id,
            username: formData.username,
            password: formData.password || undefined, // 如果没有修改密码，不传该字段
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            status: formData.status,
            balance: formData.balance,
          }
        : {
            user_id: null, // 添加用户时传 null
            username: formData.username,
            password: formData.password,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            status: formData.status,
            balance: formData.balance,
          };

      // 调用 add_user API（添加或修改都用这个接口）
      const response = await add_user(
        params.user_id,
        params.username,
        params.password,
        params.email,
        params.phone,
        params.role,
        params.status,
        params.balance
      );

      // 检查响应状态
      if (response && response.data && response.data.code === 200) {
        message.success(editUser ? "用户信息已更新！" : "用户添加成功！");

        // 返回列表页
        navigate("/user-management");
      } else {
        message.error(
          response.data?.message || (editUser ? "更新用户失败" : "添加用户失败")
        );
      }
    } catch (err) {
      console.error("操作失败:", err);

      // 详细的错误处理
      let errorMsg = "操作失败，请重试";

      if (err.response) {
        // 服务器返回了错误响应
        if (err.response.status === 404) {
          errorMsg =
            "API 接口不存在，请联系后端开发人员检查 /api/admin_add_user.php 文件";
        } else if (err.response.status === 401) {
          errorMsg = "未授权，请重新登录";
        } else if (err.response.status === 403) {
          errorMsg = "权限不足，无法执行此操作";
        } else {
          errorMsg =
            err.response.data?.message || `服务器错误 (${err.response.status})`;
        }
      } else if (err.request) {
        // 请求已发送但没有收到响应
        errorMsg = "网络错误，请检查网络连接或服务器状态";
      } else {
        // 其他错误
        errorMsg = err.message || "操作失败，请重试";
      }

      message.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/user-management");
  };

  return (
    <Layout>
      <div className="add-edit-user">
        {/* 头部 */}
        <div className="detail-header">
          <button className="btn-back" onClick={handleBack} title="返回">
            ←
          </button>
          <h1 className="page-title">{editUser ? "编辑用户" : "添加用户"}</h1>
          <div className="header-placeholder"></div>
        </div>

        {/* 表单容器 */}
        <div className="form-container">
          {/* 表单 */}
          <form className="user-form" onSubmit={handleSubmit}>
            {/* 基本信息 */}
            <div className="form-section">
              <h3 className="section-title">基本信息</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>用户名
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请输入用户名"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>邮箱
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="请输入邮箱地址"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>手机号
                  </label>
                  <PhoneInput
                    country={"cn"}
                    value={formData.phone}
                    onChange={(val) => handleInputChange("phone", val)}
                    inputClass="form-input"
                    containerClass="phone-input-container"
                    placeholder="请输入手机号码"
                    enableSearch
                    disableSearchIcon
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>用户角色
                  </label>
                  <select
                    className="form-input"
                    value={formData.role}
                    onChange={(e) => handleInputChange("role", e.target.value)}
                    required
                  >
                    <option value="user">普通用户</option>
                    <option value="admin">管理员</option>
                  </select>
                </div>
              </div>

              {/* 密码字段 */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">{!editUser && "*"}</span>密码
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder={
                      editUser ? "留空表示不修改密码" : "请输入密码（至少6位）"
                    }
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    required={!editUser}
                  />
                  <div className="form-hint">
                    {editUser
                      ? "如需修改密码，请输入新密码，否则留空"
                      : "密码长度至少为 6 位字符"}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">{!editUser && "*"}</span>
                    确认密码
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder={
                      editUser ? "请再次输入新密码" : "请再次输入密码"
                    }
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    required={!editUser}
                  />
                  <div className="form-hint">
                    {editUser
                      ? "如修改密码，请确保两次输入一致"
                      : "请确保两次输入的密码一致"}
                  </div>
                </div>
              </div>
            </div>

            {/* 账户状态 */}
            <div className="form-section">
              <h3 className="section-title">账户状态</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>状态
                  </label>
                  <select
                    className="form-input"
                    value={formData.status}
                    onChange={(e) =>
                      handleInputChange("status", parseInt(e.target.value))
                    }
                    required
                  >
                    <option value={1}>启用</option>
                    <option value={0}>禁用</option>
                  </select>
                </div>

                <div className="form-group">{/* 占位，保持布局 */}</div>
              </div>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {/* 提交按钮 */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancel}
                disabled={loading}
              >
                取消
              </button>
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? (
                  <span className="btn-loading">
                    <Loading size="small" text="" />
                    <span>提交中...</span>
                  </span>
                ) : editUser ? (
                  "保存修改"
                ) : (
                  "添加用户"
                )}
              </button>
            </div>
          </form>
        </div>

        {/* 页脚 */}
        <footer className="page-footer">
          <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
        </footer>
      </div>
    </Layout>
  );
};

export default AddEditUser;
